import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { appToast } from '../../../../components/AppToast'
import { getStudentDetail as getStudent, createStudent, updateStudent } from '../api/studentsApi'
import { uploadStudentPhoto, getStudentInstallments } from '../api/studentScreensApi'
import { loadMasterOptions } from '../api/mastersApi'

const initialFormState = {
  // Personal
  photoUrl: '',
  firstName: '',
  middleName: '',
  lastName: '',
  sendEmail: true,
  email: '',
  sendSms: true,
  mobile: '',
  landline: '',
  dob: '',
  gender: 'Select',
  caste: 'Select',
  subCaste: 'Select',
  birthPlace: '',
  language: 'Select',

  // Contact
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  city: '',
  pinCode: '',
  state: 'Select',
  place: 'Select',

  // Guardian
  gName: '',
  gRelation: 'Select',
  gSendEmail: true,
  gEmail: '',
  gSendSms: true,
  gMobile: '',
  gLandline: '',
  gOccupation: 'Select',
  gIncome: '',

  // Guardian 2
  g2Name: '',
  g2Relation: 'Select',
  g2SendEmail: true,
  g2Email: '',
  g2SendSms: true,
  g2Mobile: '',
  g2Landline: '',
  g2Occupation: 'Select',
  g2Income: '',

  // Academic
  pSchool: 'Select',
  prevBoard: 'Select',
  medium: 'Select',
  courseStd: 'Select',
  marks: '',
  rollNo: '',

  // College
  idCardNo: '',
  prnNumber: '',
  abcRegistrationNo: '',
  saralId: '',
  govAppFormNo: '',
  uniqueNo: '',
  penNo: '',
  sReservation: 'Select',
  pChallanged: 'Select',
  vacName: '',
  dose1Date: '',
  dose2Date: '',

  // Enrollment
  studentCode: '',
  registrationNo: '',
  institute: 'Select',
  branch: 'Select',
  course: 'Select',
  batch: 'Select',
  scheme: 'Select',
  status: 'Active',
  feeStatus: 'Unpaid',
  admissionDate: '',
  validTill: '',
  counsellor: 'Select',
  source: 'Select',

  // Fees
  feeAdmission: '',
  feesTuition: '',
  feeCourseware: '',
  discAmt: '',
  srTaxYN: false,
  taxPercent: 'Select',
  srTaxAmt: '',
  totalAmount: '',
  installments: '',

  // Fees → installment schedule (persisted on save via installmentSchedule)
  feesInstallments: [],
  feesInstallmentsDirty: false,

  // UDFs & Notes
  udf1: '', udf2: '', udf3: '', udf4: '', udf5: '',
  udm1: 'Select', udm2: 'Select', udm3: 'Select',
  feesUdf1: '', feesUdf2: '', feesUdf3: '', feesUdf4: '', feesUdf5: '', feesUdf6: '',
  feesUdf7: '', feesUdf8: '', feesUdf9: '', feesUdf10: '', feesUdf11: '', feesUdf12: '',
  particulars: '',
  notes: '',

  // Audit (stripped from save payload; populated by API on load)
  createdBy: 'Admin',
  createdDate: '12 May 2026 @ 10:30 AM',
  updatedBy: 'Admin',
  updatedDate: '15 Jun 2026 @ 02:15 PM',
}

const isDataUrl = (value) => typeof value === 'string' && value.startsWith('data:')

function dataUrlToFile(dataUrl, filename = 'profile-photo.jpg') {
  const arr = dataUrl.split(',')
  const mime = (arr[0].match(/:(.*?);/) || [])[1] || 'image/jpeg'
  const base64 = (arr[1] || '').replace(/\s+/g, '')
  const binary = atob(base64)
  const u8arr = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) u8arr[i] = binary.charCodeAt(i)
  return new File([u8arr], filename, { type: mime })
}

export function useStudentsEntryScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [form, setForm] = useState(initialFormState)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [studentId, setStudentId] = useState(null)
  const [viewMode, setViewMode] = useState('create')
  const [activeEditSection, setActiveEditSection] = useState(null)
  const [options, setOptions] = useState({})

  useEffect(() => {
    loadMasterOptions().then(setOptions).catch(() => {})
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const id = params.get('id')
    const copyFrom = params.get('copyFrom')
    const mode = String(params.get('mode') || '').toLowerCase()

    if (copyFrom || mode === 'admit') {
      const sourceId = copyFrom || id
      setStudentId(null)
      setViewMode('create')
      loadStudentDataForAdmit(sourceId)
    } else if (id) {
      setStudentId(id)
      loadStudentData(id)
      setViewMode(mode === 'view' ? 'view' : 'edit')
    } else {
      setStudentId(null)
      setViewMode('create')
      setForm(prev => ({ ...prev, studentCode: 'STU-NEW-' + Date.now().toString().slice(-4) }))
    }
  }, [location.search])

  const loadStudentDataForAdmit = async (sourceId) => {
    setIsLoading(true)
    setError('')
    try {
      const data = await getStudent(sourceId)
      const cloned = { ...data }
      // Reset student ID, enrollment codes, and identifiers per legacy CopyStudent
      delete cloned.id
      delete cloned.ID
      delete cloned.acHeadId
      delete cloned.AcHeadID
      delete cloned.studentCode
      delete cloned.registrationNo
      delete cloned.rollNo
      delete cloned.idCardNo
      delete cloned.prnNumber
      delete cloned.abcRegistrationNo
      delete cloned.saralId
      delete cloned.govAppFormNo
      delete cloned.uniqueNo
      delete cloned.penNo

      // Reset financial balances and installment schedules
      cloned.feeAdmission = ''
      cloned.feesTuition = ''
      cloned.feeCourseware = ''
      cloned.discAmt = ''
      cloned.srTaxAmt = ''
      cloned.totalAmount = ''
      cloned.feesInstallments = []
      cloned.feesInstallmentsDirty = false
      cloned.feeStatus = 'Unpaid'
      cloned.status = 'Active'

      // Reset academic course & batch for new admission
      cloned.course = 'Select'
      cloned.batch = 'Select'
      cloned.scheme = 'Select'

      // Fresh student code
      cloned.studentCode = 'STU-NEW-' + Date.now().toString().slice(-4)

      setForm((prev) => ({ ...prev, ...cloned }))
      appToast.info(`Loaded demographic details for new admission from student #${data.studentCode || sourceId}.`)
    } catch (err) {
      setError('Failed to load student demographic data for admission.')
    } finally {
      setIsLoading(false)
    }
  }

  const loadStudentData = async (id) => {
    setIsLoading(true)
    setError('')
    try {
      const data = await getStudent(id)
      setForm((prev) => ({ ...prev, ...data }))
      await loadInstallments(id)
    } catch (err) {
      setError('Failed to load student data.')
    } finally {
      setIsLoading(false)
    }
  }

  const loadInstallments = async (id) => {
    try {
      const rows = await getStudentInstallments(id)
      patchForm(
        'feesInstallments',
        (rows || []).map((r) => ({
          id: Number(r.id) || 0,
          dueDate: r.dueDate || '',
          particulars: r.particulars || '',
          amount: Number(r.amount) || 0,
        }))
      )
    } catch {
      patchForm('feesInstallments', [])
    }
    patchForm('feesInstallmentsDirty', false)
  }

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const refreshStudentCode = () =>
    setForm((prev) => ({ ...prev, studentCode: 'STU-NEW-' + Date.now().toString().slice(-4) }))

  const resetForNextStudent = () => {
    setForm({ ...initialFormState })
    setStudentId(null)
    setViewMode('create')
    setSuccess('')
    setError('')
    refreshStudentCode()
  }

  const handleSave = async () => {
    setError('')
    setSuccess('')

    if (!form.firstName || !form.lastName || !form.mobile || !form.gender || form.gender === 'Select') {
      setError('Please fill in all required fields (marked with *).')
      return { success: false }
    }

    setIsSaving(true)
    try {
      const payload = { ...form }
      const hasPickedPhoto = isDataUrl(payload.photoUrl)
      delete payload.photoUrl
      delete payload.createdBy
      delete payload.createdDate
      delete payload.updatedBy
      delete payload.updatedDate

      if (payload.feesInstallmentsDirty) {
        payload.installmentSchedule = (payload.feesInstallments || []).map((r) => ({
          id: Number(r.id) || 0,
          dueDate: r.dueDate || null,
          particulars: r.particulars || '',
          amount: Number(r.amount) || 0,
        }))
      }
      delete payload.feesInstallments
      delete payload.feesInstallmentsDirty

      let savedId = studentId
      if (studentId) {
        await updateStudent(studentId, payload)
        setSuccess('Student updated successfully.')
      } else {
        const newStudent = await createStudent(payload)
        savedId = newStudent.id
        setStudentId(newStudent.id)
        setSuccess('Student created successfully.')
      }

      if (hasPickedPhoto && savedId) {
        try {
          const file = dataUrlToFile(form.photoUrl)
          const res = await uploadStudentPhoto(savedId, file)
          const baseUrl = (res && res.photoUrl) || form.photoUrl
          patchForm('photoUrl', baseUrl + (baseUrl && !baseUrl.startsWith('data:') ? '?t=' + Date.now() : ''))
        } catch {
          // photo upload failed but student saved
        }
      }

      // Always reload student data to get latest info from backend (including photo)
      // This handles both create (new ID) and update (existing ID) cases
      await loadStudentData(savedId)
      
      return { success: true, id: savedId }
    } catch (err) {
      setError('Failed to save student. Please try again.')
      return { success: false }
    } finally {
      setIsSaving(false)
    }
  }

  const completionPercent = useMemo(() => {
    const totalFields = Object.keys(initialFormState).length
    const filledFields = Object.values(form).filter(
      (val) =>
        val !== '' &&
        val !== 'Select' &&
        val !== false &&
        !Array.isArray(val) &&
        (typeof val === 'string' || typeof val === 'number')
    ).length
    return Math.round((filledFields / totalFields) * 100)
  }, [form])

  return {
    state: {
      form,
      isLoading,
      isSaving,
      error,
      success,
      studentId,
      viewMode,
      isEditMode: viewMode === 'edit',
      isViewMode: viewMode === 'view',
      isFormEditable: viewMode !== 'view',
      completionPercent,
      activeEditSection,
      options: {
        institutes: ['Select', ...(options.institutes || [])],
        branches: ['Select', ...(options.branches || [])],
        courses: ['Select', ...(options.courses || [])],
        courses_previous: ['Select', ...(options.courses_previous || [])],
        batches: ['Select', ...(options.batches || [])],
        schemes: ['Select', ...(options.schemes || [])],
        sources: ['Select', ...(options.sources || [])],
        counsellors: ['Select', ...(options.counsellors || [])],
        academic_years: ['Select', ...(options.academic_years || [])],
        subjects: ['Select', ...(options.subjects || [])],
        caste: ['Select', ...(options.caste || [])],
        subcaste: ['Select', ...(options.subcaste || [])],
        language: ['Select', ...(options.language || [])],
        religions: ['Select', ...(options.religions || [])],
        relations: ['Select', ...(options.relations || [])],
        occupations: ['Select', ...(options.occupations || [])],
        states: ['Select', ...(options.states || [])],
        places: ['Select', ...(options.places || [])],
        schools: ['Select', ...(options.schools || [])],
        boards: ['Select', ...(options.boards || [])],
        mediums: ['Select', ...(options.mediums || [])],
      },
    },
    actions: {
      patchForm,
      handleSave,
      resetForNextStudent,
      setViewMode,
      openEditSection: (section) => setActiveEditSection(section),
      closeEditSection: () => setActiveEditSection(null),
    },
  }
}
